import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FiSearch, FiShoppingCart } from "react-icons/fi";
import SafeImage from "../../ui/SafeImage";
import { useCart } from "../../context/CartContext";
import { useToast } from "../../context/ToastContext";
import { getBaskets, getFarms } from "../../Utils/storeService";
import { formatLek } from "../../Utils/currency";
import { useDebounce } from "../../hooks/useDebounce";
import {
  isLowAvailability,
  isSoldOut,
  readAvailability,
} from "../../Utils/availability";
import * as S from "./BasketsList.styled";

/**
 * Every basket on the marketplace, in one place. The mobile bottom nav points here — before
 * this page existed there was no basket index at all, only per-farm lists and `/baskets/:id`.
 */
const BasketsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addItemToCart, cartItems = [] } = useCart();
  const { success: toastSuccess } = useToast();

  const [baskets, setBaskets] = useState([]);
  const [farms, setFarms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [params, setParams] = useSearchParams();
  const search = params.get("q") || "";
  const sort = ["price-asc", "price-desc"].includes(params.get("sort")) ? params.get("sort") : "default";
  const availableOnly = params.get("available") === "1";
  const setFilter = (key, value) => setParams((previous) => {
    const next = new URLSearchParams(previous);
    if (value) next.set(key, value);
    else next.delete(key);
    return next;
  }, { replace: true });
  const clearFilters = () => setParams((previous) => {
    const next = new URLSearchParams(previous);
    ["q", "sort", "available"].forEach((key) => next.delete(key));
    return next;
  }, { replace: true });
  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    getBaskets(undefined, { pageSize: 100 })
      .then((data) => {
        if (active) setBaskets(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [attempt]);

  // The basket payload carries farmId but no farm name, so the name is looked up
  // here. Decoration only: a failure must never keep the baskets off the page.
  useEffect(() => {
    let active = true;
    getFarms()
      .then((data) => {
        if (active) setFarms(Array.isArray(data) ? data : []);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [attempt]);

  const farmsById = useMemo(
    () => new Map(farms.map((farm) => [Number(farm.id), farm])),
    [farms]
  );

  const farmNameFor = useCallback(
    (basket) =>
      basket.farmName ||
      basket.farm?.name ||
      farmsById.get(Number(basket.farmId))?.name ||
      "",
    [farmsById]
  );

  const visible = useMemo(() => {
    const normalize = (value) => (value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    const query = normalize(debouncedSearch.trim());
    const matches = baskets.filter((basket) => {
      if (availableOnly && isSoldOut(readAvailability(basket))) return false;
      return !query || [basket.name, basket.description, ...((basket.items || []).map((item) => item.productName))]
        .some((value) => normalize(value).includes(query));
    });
    if (sort === "price-asc") matches.sort((a, b) => Number(a.price) - Number(b.price));
    if (sort === "price-desc") matches.sort((a, b) => Number(b.price) - Number(a.price));
    return matches;
  }, [baskets, debouncedSearch, availableOnly, sort]);

  const handleAdd = useCallback(
    (basket) => {
      const added = addItemToCart({
        id: basket.id,
        farmId: basket.farmId,
        farmName: farmNameFor(basket),
        name: basket.name,
        price: Number(basket.price),
        image: basket.imageUrl || "",
        availableQuantity: basket.availableQuantity,
      });
      if (added) toastSuccess(t("baskets_featured.added"));
    },
    [addItemToCart, farmNameFor, toastSuccess, t]
  );

  const searchLabel = t("baskets_page.search_placeholder", {
    defaultValue: "Search baskets",
  });

  return (
    <S.Page>
      <S.Inner>
        <S.Header>
          <S.Title>{t("baskets_page.title", { defaultValue: "Baskets" })}</S.Title>
          <S.Subtitle>
            {t("baskets_page.subtitle", {
              defaultValue: "Curated baskets of fresh produce from local farms.",
            })}
          </S.Subtitle>
          <S.SearchWrap>
            <FiSearch size={18} aria-hidden="true" />
            <S.SearchInput
              type="search"
              value={search}
              onChange={(event) => setFilter("q", event.target.value)}
              placeholder={searchLabel}
              aria-label={searchLabel}
            />
          </S.SearchWrap>
          <S.FilterRow>
            <S.SortLabel>
              {t("baskets_page.sort", { defaultValue: "Sort by" })}
              <S.SortSelect value={sort} onChange={(event) => setFilter("sort", event.target.value === "default" ? "" : event.target.value)}>
                <option value="default">{t("baskets_page.sort_default", { defaultValue: "Default order" })}</option>
                <option value="price-asc">{t("baskets_page.price_low", { defaultValue: "Price: low to high" })}</option>
                <option value="price-desc">{t("baskets_page.price_high", { defaultValue: "Price: high to low" })}</option>
              </S.SortSelect>
            </S.SortLabel>
            <S.StockFilter>
              <input type="checkbox" checked={availableOnly} onChange={(event) => setFilter("available", event.target.checked ? "1" : "")} />
              {t("baskets_page.available_only", { defaultValue: "Hide sold-out baskets" })}
            </S.StockFilter>
            {(search || availableOnly || sort !== "default") && (
              <S.SecondaryButton type="button" onClick={clearFilters}>
                {t("baskets_page.clear_filters", { defaultValue: "Clear filters" })}
              </S.SecondaryButton>
            )}
          </S.FilterRow>
        </S.Header>

        {loading ? (
          <S.Grid aria-busy="true">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <S.SkeletonCard key={i} aria-hidden="true" />
            ))}
          </S.Grid>
        ) : error ? (
          <S.EmptyState role="alert">
            <p>{t("baskets_page.load_error", { defaultValue: "We couldn't load the baskets. Please try again." })}</p>
            <S.AddButton type="button" onClick={() => setAttempt((value) => value + 1)}>
              {t("baskets_page.retry", { defaultValue: "Try again" })}
            </S.AddButton>
          </S.EmptyState>
        ) : visible.length === 0 ? (
          <S.EmptyState role="status">
            <p>{baskets.length > 0
              ? t("baskets_page.no_results", {
                  defaultValue: "No baskets match these filters. Try another search or clear the filters.",
                })
              : t("baskets_page.empty", {
                  defaultValue: "Farms will publish their baskets here when they are ready. You can explore the farms in the meantime.",
                })}</p>
            {baskets.length > 0 ? (
              <S.SecondaryButton type="button" onClick={clearFilters}>
                {t("baskets_page.clear_filters", { defaultValue: "Clear filters" })}
              </S.SecondaryButton>
            ) : (
              <S.AddButton as={Link} to="/farms">{t("baskets.browse_farms", { defaultValue: "Browse Farms" })}</S.AddButton>
            )}
          </S.EmptyState>
        ) : (
          <S.Grid>
            {visible.map((basket) => {
              const available = readAvailability(basket);
              const soldOut = isSoldOut(available);
              const inCart = cartItems.find((item) => String(item.id) === String(basket.id))?.quantity || 0;
              const atLimit = available !== null && inCart >= available;
              return (
              <S.Card key={basket.id}>
                <S.CardMedia
                  type="button"
                  onClick={() => navigate(`/baskets/${basket.id}`)}
                  aria-label={t("baskets_page.view", {
                    name: basket.name,
                    defaultValue: "View {{name}}",
                  })}
                >
                  <SafeImage src={basket.imageUrl} alt={basket.name} aspectRatio="3 / 2" />
                </S.CardMedia>
                <S.CardBody>
                  <S.Name>{basket.name}</S.Name>
                  {farmNameFor(basket) ? (
                    <S.FarmLine as={Link} to={`/farms/${basket.farmId}`}>
                      {t("baskets_page.from_farm", {
                        name: farmNameFor(basket),
                        defaultValue: "from {{name}}",
                      })}
                    </S.FarmLine>
                  ) : null}
                  {basket.description ? (
                    <S.Description>{basket.description}</S.Description>
                  ) : null}
                  {basket.items?.length ? (
                    <S.ChipRow>
                      {basket.items.slice(0, 3).map((item) => (
                        <S.Chip key={item.productId ?? item.productName}>
                          {item.productName} ×{item.quantity}
                        </S.Chip>
                      ))}
                      {basket.items.length > 3 ? (
                        <S.Chip>+{basket.items.length - 3}</S.Chip>
                      ) : null}
                    </S.ChipRow>
                  ) : null}
                  {soldOut ? (
                    <S.Availability $variant="danger">
                      {t("baskets.sold_out", { defaultValue: "Sold out" })}
                    </S.Availability>
                  ) : isLowAvailability(available) ? (
                    <S.Availability $variant="warning">
                      {t("baskets.only_left", {
                        count: available,
                        defaultValue: "Only {{count}} left",
                      })}
                    </S.Availability>
                  ) : null}
                  {inCart > 0 && <S.Description>{t("baskets_page.in_cart", { count: inCart, defaultValue: "In your cart: {{count}}" })}</S.Description>}
                  <S.Footer>
                    <S.Price>{formatLek(basket.price)}</S.Price>
                    <S.AddButton
                      type="button"
                      onClick={() => handleAdd(basket)}
                      disabled={soldOut || atLimit}
                    >
                      <FiShoppingCart size={14} aria-hidden="true" />
                      {soldOut
                        ? t("baskets.sold_out", { defaultValue: "Sold out" })
                        : atLimit
                          ? t("baskets_page.cart_limit", { defaultValue: "Limit reached" })
                          : t("marketplace.add_to_cart", { defaultValue: "Add to Cart" })}
                    </S.AddButton>
                  </S.Footer>
                </S.CardBody>
              </S.Card>
              );
            })}
          </S.Grid>
        )}
      </S.Inner>
    </S.Page>
  );
};

export default BasketsList;
